'use client'
import { ButtonRoundStyle, Form, RHFTextField } from '@/components'
import { TitleLoginForm } from '@/features/auth/components'
import { setInitialPasswordService } from '@/features/auth/services'
import { useAccountStore } from '@/store'
import yup from '@/utils/yup-global'
import { setInitialPasswordSchema } from '@/validations'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Stack, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

type Props = {
  onSuccess?: () => void
  haveTitle?: boolean
}

export const SetInitialPasswordForm = ({ onSuccess, haveTitle = true }: Props) => {
  const t = useTranslations('UserProfile')
  const [isPending, startTransition] = useTransition()
  const { updateUser } = useAccountStore()

  const method = useForm<yup.InferType<typeof setInitialPasswordSchema>>({
    resolver: yupResolver(setInitialPasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  })

  const { handleSubmit, reset } = method

  const onSubmit = async (values: yup.InferType<typeof setInitialPasswordSchema>) => {
    startTransition(async () => {
      try {
        const result = await setInitialPasswordService(values.newPassword)

        if (result.status === 'error') {
          toast.error(result.message)
          return
        }

        toast.success(result.message || 'Cập nhật mật khẩu thành công!')
        reset()

        // Cập nhật lại thông tin user để refresh is_phone_registered status
        await updateUser()

        // Gọi callback success nếu có
        onSuccess?.()
      } catch (error: any) {
        console.error('Error setting initial password:', error)
        toast.error('Có lỗi xảy ra khi cập nhật mật khẩu')
      }
    })
  }

  return (
    <Stack spacing={2}>
      {haveTitle && (
        <Box>
          <TitleLoginForm title={t('Cập nhật mật khẩu lần đầu')} />
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: {
                xs: '0.75rem',
                md: '0.875rem',
              },
              fontWeight: 300,
            }}
          >
            {t.rich('Để bảo mật tài khoản của bạn, vui lòng tạo mật khẩu mới cho lần đăng nhập tiếp theo', {
              bold: (chunks) => <span style={{ fontWeight: 'bold' }}>{chunks}</span>,
            })}
          </Typography>
        </Box>
      )}

      <Form methods={method} onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <RHFTextField
            autoFocus
            name="newPassword"
            type="password"
            label={t('Mật khẩu mới')}
            placeholder={t('Nhập mật khẩu mới')}
            required
            disabled={isPending}
          />

          <RHFTextField
            name="confirmPassword"
            type="password"
            label={t('Xác nhận mật khẩu')}
            placeholder={t('Nhập lại mật khẩu mới')}
            required
            disabled={isPending}
          />

          <ButtonRoundStyle variant="contained" type="submit" disabled={isPending} sx={{ mt: 2 }}>
            {t('Cập nhật mật khẩu')}
          </ButtonRoundStyle>
        </Stack>
      </Form>
    </Stack>
  )
}
